import { useEffect, useState } from 'react';
import styles from 'styles/modules/popUps/Uploader.module.scss';
import UploaderSelect from './Uploader/UploaderSelect';
import UploaderSettings from './Uploader/UploaderSettings';
import UploaderProgress from './Uploader/UploaderProgress';
import UploaderCropper from './Uploader/UploaderCropper';
import { getAllowedTypes, getFileType, readFile } from 'helpers/file';
import { cropImage } from 'helpers/canvas';

const MAX_FILE_SIZE = 50 * 1024 * 1024;

export default function Uploader({
    isActive = false,
    croppable = false,
    onSubmit,
    onCancel,
    returnFile = false,
    noSettings = true,
    allowedTypes = 'images',
    forceCrop = false,
}) {
    const [file, setFile] = useState(null);
    const [fileSrc, setFileSrc] = useState(null);
    const [fileSettings, setFileSettings] = useState({
        title: noSettings ? Math.random().toString(36).substring(3) : null,
    });
    const [uploaded, setUploaded] = useState(false);

    const [uploadError, setUploadError] = useState(false);
    const [currentStep, setCurrentStep] = useState('select');
    const [cropped, setCropped] = useState(!croppable);
    const allowedFileTypes = getAllowedTypes(allowedTypes);
    const [error, setError] = useState(null);
    const [forceCropped, setForceCropped] = useState(null);

    const handleChangeSelect = async (selectedFile) => {
        if (!selectedFile) return null;
        const fileExt = getFileType(selectedFile.type);
        const allowed = allowedFileTypes.includes(fileExt);
        const tooBig = selectedFile.size > MAX_FILE_SIZE;

        if (!allowed) {
            setError('This file type is not allowed.');
        } else if (tooBig) {
            setError('Selected file is too big. Maximum upload size is 50MB.');
        } else {
            if (selectedFile.type.match('image.*')) {
                let imageDataUrl = await readFile(selectedFile);
                setFileSrc(imageDataUrl);
            }

            setFile(selectedFile);
            setError(null);
        }
    };

    const handleForceCrop = (img) => {
        setFile(img.file);
        setFileSrc(img.src);
        setForceCropped(true);
    };

    const handleChangeCropper = (img) => {
        setFile(img.file);
        setFileSrc(img.src);

        setCropped(true);
    };

    const handleConfirmSettings = (settings) => {
        setFileSettings(settings);
    };

    const handleProgressSuccess = () => {
        setUploaded(true);
    };

    const handleProgressError = () => {
        setUploadError(true);
    };

    const handleConfirmSuccess = () => {
        if (returnFile) {
            onSubmit({
                file: file,
                src: fileSrc,
            });
        } else {
            onSubmit();
        }
        // setError(null);
        resetPopup();
    };

    const handleCancel = () => {
        resetPopup();
        onCancel();
    };

    const handleOverlayClick = (e) => {
        e.stopPropagation();
        handleCancel();
    };

    const resetPopup = () => {
        setError(null);
        setFile(null);
        setFileSrc(null);
        setError(false);
        setForceCropped(false);
        setFileSettings({
            title: noSettings ? Math.random().toString(36).substring(3) : null,
        });
        setUploaded(false);
        setUploadError(false);
        setCurrentStep('select');
        setCropped(!croppable);
    };

    const steps = {
        select: {
            title: 'Select a file',
            component: <UploaderSelect onChange={handleChangeSelect} />,
        },
        crop: {
            title: 'Crop your image',
            component: (
                <UploaderCropper
                    fileSrc={fileSrc}
                    file={file}
                    onSubmit={handleChangeCropper}
                />
            ),
        },
        settings: {
            title: 'File settings',
            component: (
                <UploaderSettings
                    fileSrc={fileSrc}
                    file={file}
                    onSubmit={handleConfirmSettings}
                />
            ),
        },
        uploading: {
            title: 'Uploading file',
            component: (
                <UploaderProgress
                    file={file}
                    onSuccess={handleProgressSuccess}
                    onError={handleProgressError}
                    settings={fileSettings}
                />
            ),
        },
        uploading_error: {
            title: 'Uploading error',
            component: null,
        },
    };

    useEffect(() => {
        if (!file) {
            setCurrentStep('select');
        } else if (forceCrop && !forceCropped) {
            cropImage(fileSrc, 1).then((croppedFile) => {
                handleForceCrop(croppedFile);
            });
            // setForceCropped(fileSrc);
        } else if (file && !cropped && !forceCrop) {
            setCurrentStep('crop');
        } else if (
            (cropped || forceCropped) &&
            !noSettings &&
            !fileSettings.title
        ) {
            setCurrentStep('settings');
        } else if (!uploaded && !uploadError && !returnFile) {
            setCurrentStep('uploading');
        } else if (!uploaded && !returnFile && uploadError) {
            console.error('step: ', 'uploadError');
            setCurrentStep('uploading_error');
        } else {
            handleConfirmSuccess();
        }
    }, [file, fileSettings, uploaded, uploadError, cropped]);

    useEffect(() => {
        const handleKeyUp = (e) => {
            const pressed = e.key?.toLowerCase();
            if (pressed === 'escape') {
                handleCancel();
            }
        };
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, []);

    return (
        <div
            className={[styles.overlay, isActive ? styles.isActive : null].join(
                ' '
            )}
            onClick={handleOverlayClick}
        >
            <div
                className={styles.wrapper}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles.header}>
                    <div className={styles.title}>
                        {steps[currentStep].title}
                    </div>
                    <button
                        className={styles.close}
                        onClick={handleCancel}
                        type="button"
                    >
                        Close
                    </button>
                </div>
                {error && <div className={styles.error}>{error}</div>}
                {steps[currentStep].component}
            </div>
        </div>
    );
}
